import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

export const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Destinations", path: "/destinations" },
  { label: "Opportunities", path: "/opportunities" },
  { label: "Developments", path: "/developments" },
  { label: "Advisory", path: "/advisory" },
  { label: "Insights", path: "/insights" },
  { label: "About", path: "/about" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const testId = (label) => `nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[rgba(11,12,16,0.85)] backdrop-blur-xl border-b border-[rgba(197,168,128,0.15)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Logo />
        <nav className="hidden xl:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === "/"}
              data-testid={testId(l.label)}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? "text-[#E6CA65]" : "text-[#9DA3AE] hover:text-[#F4F3EF]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/private-consultation"
            data-testid="nav-cta-private-consultation"
            className="text-xs uppercase tracking-[0.14em] font-semibold px-6 py-3 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F] hover:shadow-[0_0_24px_rgba(197,168,128,0.45)] transition-shadow duration-300"
          >
            Private Consultation
          </Link>
        </nav>
        <button
          className="xl:hidden text-[#F4F3EF] p-2"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <div
          className="xl:hidden fixed inset-0 top-20 z-40 backdrop-blur-2xl border-t border-[rgba(197,168,128,0.15)]"
          style={{ backgroundColor: "rgba(10, 10, 10, 0.97)" }}
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col gap-2 px-8 pt-10" aria-label="Mobile">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === "/"}
                data-testid={`mobile-${testId(l.label)}`}
                className={({ isActive }) =>
                  `font-display text-3xl py-3 border-b border-[rgba(197,168,128,0.12)] ${
                    isActive ? "text-[#E6CA65]" : "text-[#F4F3EF]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/private-consultation"
              data-testid="mobile-nav-cta-private-consultation"
              className="mt-8 text-center text-sm uppercase tracking-[0.14em] font-semibold px-6 py-4 rounded-full text-[#0B0C10] bg-gradient-to-r from-[#E6CA65] via-[#C5A880] to-[#9A7B4F]"
            >
              Private Consultation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
